import { DEFAULT_LOCALE, type Locale } from "../../../src/generate/conceptCopy.js";

/**
 * PWA shell strings (not engine `COPY` / not mini-game deck prose).
 * Keep keys parallel across locales.
 */
export type PwaUi = {
  appName: string;
  language: string;
  languageAria: string;
  /** Learner profile (M.2: single "guest" + dropdown shell for M.4). */
  profile: string;
  profileAria: string;
  profileOptionGuest: string;
  subject: string;
  subjectAria: string;
  subjectOptionAiLiteracy: string;
  subjectOptionFirstAid: string;
  subjectOptionNotany: string;
  subjectOptionEpsoAd8Ai: string;
  /** Shown on home when first-aid pack is active. */
  subjectFirstAidNote: string;
  /** Shown on home when the notany (meta / pitch) pack is active. */
  subjectNotanyNote: string;
  /** Shown on home when the EPSO AD8 AI practice pack is active. */
  subjectEpsoAd8AiNote: string;
  /** Linear learning path (M.5). */
  questPathLabel: string;
  questPathAiLiteracy: string;
  questPathFirstAid: string;
  questPathNotany: string;
  questPathEpsoAd8Ai: string;
  questStepLine: (step: number, total: number, conceptName: string) => string;
  questTargetHint: (minElo: string) => string;
  questArcComplete: string;
  questArcAria: string;
  gameStyle: string;
  gameStyleAria: string;
  gmDefault: string;
  gmPatient: string;
  gmDrill: string;
  gmHomeHintPatient: string;
  gmHomeHintDrill: string;
  ratingLabel: string;
  streakLabelAria: string;
  streakNone: string;
  streakDayStreak: (n: number) => string;
  profileStats: string;
  sessionStats: string;
  finalStats: string;
  weakestConceptAria: string;
  focusPrefix: string;
  pickFormatHint: string;
  resetProgress: string;
  cardMcqTitle: string;
  cardMcqSub: string;
  cardBackpackTitle: string;
  cardBackpackSub: string;
  cardSpotlieTitle: string;
  cardSpotlieSub: string;
  cardRedactTitle: string;
  cardRedactSub: string;
  suggestedBadge: string;
  sessionCounter: (correct: number, total: number) => string;
  /** Mini-game session summary (after SESSION_LENGTH rounds). */
  miniGameSummaryDetail: (
    perfectRounds: number,
    totalRounds: number,
    weightedPct: number
  ) => string;
  answerOptionsAria: string;
  hintMcq: string;
  footer: string;
  seeSummary: string;
  nextQuestion: string;
  home: string;
  backHome: string;
  sessionSummary: string;
  newSession: string;
  summaryScoreLine: (correct: number, total: number, accuracy: number) => string;
  hintSummary: string;
  feedbackCorrect: string;
  feedbackWrong: string;
  authSignIn: string;
  authSignOut: string;
  authSignedInAs: string;
  authSettings: string;
  authModalTitle: string;
  authModalEmailLabel: string;
  authModalSendButton: string;
  authModalSentTitle: string;
  authModalSentBody: string;
  authModalCodeLabel: string;
  authModalVerifyButton: string;
  authModalErrorPrefix: string;
  authModalCancel: string;
  authAuthFailedExpired: string;
  authMigratedToast: string;
  authSyncFailedToast: string;
  authDeletedToast: string;
  authSignedOutEmpty: string;
  authSignedOutCta: string;
  privacyTitle: string;
  privacyIntro: string;
  privacyWhatWeDelete: string;
  privacyWhatWeDeleteBody: string;
  privacyWhatWeKeepAnon: string;
  privacyWhatWeKeepAnonBody: string;
  privacyHowToDelete: string;
  privacyHowToDeleteBody: string;
  privacyBackHome: string;
  footerPrivacyLink: string;
  footerAboutLink: string;
  settingsTitle: string;
  settingsBackToHome: string;
  settingsProfile: string;
  settingsEmailLabel: string;
  settingsLocaleLabel: string;
  settingsGameMasterLabel: string;
  settingsSubjectLabel: string;
  settingsDangerZone: string;
  settingsDangerZoneBody: string;
  settingsDeleteAccountCta: string;
  deleteAccountModalTitle: string;
  deleteAccountModalBodyDeleted: string;
  deleteAccountModalBodyRetained: string;
  deleteAccountModalConfirmLabel: string;
  deleteAccountModalCancel: string;
  deleteAccountModalSubmit: string;
  deleteAccountModalError: string;
};

const en: PwaUi = {
  appName: "NotAnyAI",
  language: "Language",
  languageAria: "Language",
  profile: "Profile",
  profileAria: "Active learner profile",
  profileOptionGuest: "Guest",
  subject: "Subject",
  subjectAria: "Active course or topic",
  subjectOptionAiLiteracy: "AI literacy",
  subjectOptionFirstAid: "First aid (short course)",
  subjectOptionNotany: "What is notanyai? (demo)",
  subjectOptionEpsoAd8Ai: "EPSO AD8 AI (practice)",
  subjectFirstAidNote:
    "Practical tips only — not a substitute for certified first-aid training or medical advice.",
  subjectNotanyNote:
    "A demo course about notanyai itself — you learn what the platform does by using it.",
  subjectEpsoAd8AiNote:
    "Practice items only — not EPSO’s question bank and not an official course.",
  questPathLabel: "Learning path",
  questPathAiLiteracy: "Starter track",
  questPathFirstAid: "First-response habits",
  questPathNotany: "Product tour",
  questPathEpsoAd8Ai: "Ranking-test track",
  questStepLine: (step, total, name) => `Step ${step} of ${total} — ${name}`,
  questTargetHint: (n) => `Reach about ${n}+ on this concept.`,
  questArcComplete: "You’ve finished this path. Keep practicing in any format.",
  questArcAria: "Current learning path step",
  gameStyle: "Style",
  gameStyleAria: "Tutor difficulty and practice style",
  gmDefault: "Balanced",
  gmPatient: "Patient",
  gmDrill: "Intense",
  gmHomeHintPatient:
    "Gentle pace: we hide the streak on this screen. Clearer help when you get an answer wrong.",
  gmHomeHintDrill:
    "Tough practice: similar wrong answers, and the quiz may repeat recent questions.",
  ratingLabel: "Rating",
  streakLabelAria: "Daily streak",
  streakNone: "no streak",
  streakDayStreak: (n) => `${n}-day streak`,
  profileStats: "Profile stats",
  sessionStats: "Session stats",
  finalStats: "Final stats",
  weakestConceptAria: "Weakest concept",
  focusPrefix: "Focus suggestion:",
  pickFormatHint: "Pick a format. Press <kbd>1</kbd>–<kbd>4</kbd> to jump in.",
  resetProgress: "Reset progress",
  cardMcqTitle: "Quick quiz",
  cardMcqSub: "4-option questions tuned to your level.",
  cardBackpackTitle: "Fill the Backpack",
  cardBackpackSub: "Pick the prompt fragments that belong.",
  cardSpotlieTitle: "Spot the Lie",
  cardSpotlieSub: "Catch the confident-but-wrong claims.",
  cardRedactTitle: "Redact Before Send",
  cardRedactSub: "Hide the sensitive bits before pasting.",
  suggestedBadge: "Suggested",
  sessionCounter: (c, t) => `Session ${c}/${t}`,
  miniGameSummaryDetail: (perfect, total, weightedPct) =>
    `${perfect} of ${total} rounds fully correct — ${weightedPct}% weighted accuracy.`,
  answerOptionsAria: "Answer options",
  hintMcq:
    "Tap an option or press <kbd>1</kbd>–<kbd>4</kbd>. Press <kbd>N</kbd> for next, <kbd>H</kbd> for home.",
  footer: "Saves in this browser. Add to home screen for a quick quiz.",
  seeSummary: "See summary",
  nextQuestion: "Next question",
  home: "Home",
  backHome: "← Home",
  sessionSummary: "Session summary",
  newSession: "New session",
  summaryScoreLine: (c, t, a) => `${c} / ${t} correct (${a}%).`,
  hintSummary: "Press <kbd>N</kbd> for new session, <kbd>H</kbd> for home.",
  feedbackCorrect: "Correct — well done.",
  feedbackWrong: "Not quite. Right answer highlighted above.",
  authSignIn: "Sign in",
  authSignOut: "Sign out",
  authSignedInAs: "Signed in as {email}",
  authSettings: "Settings",
  authModalTitle: "Sign in to save your progress",
  authModalEmailLabel: "Email",
  authModalSendButton: "Send code",
  authModalSentTitle: "Check your inbox",
  authModalSentBody: "We sent a one-time code to {email}.",
  authModalCodeLabel: "One-time code",
  authModalVerifyButton: "Verify code",
  authModalErrorPrefix: "Couldn't sign in: ",
  authModalCancel: "Cancel",
  authAuthFailedExpired: "This sign-in link expired. Please request a new one.",
  authMigratedToast: "Welcome - your progress is saved to your account.",
  authSyncFailedToast: "Couldn't sync with the cloud. Playing in local mode for now.",
  authDeletedToast: "Your account has been deleted.",
  authSignedOutEmpty: "Sign in to continue your progress.",
  authSignedOutCta: "Sign in",
  privacyTitle: "Privacy",
  privacyIntro:
    "NotAnyAI is a learning tool. We keep the minimum data needed to track your progress, and we hard-delete personal data when you ask us to.",
  privacyWhatWeDelete: "What we delete on request",
  privacyWhatWeDeleteBody:
    "When you delete your account, we permanently remove your sign-in identity, your profile (display name, locale, active subject), and your engine state (rating, streak, mastery per concept). This cannot be undone.",
  privacyWhatWeKeepAnon: "What we keep, anonymized",
  privacyWhatWeKeepAnonBody:
    "Your past responses are kept without any link to you. The user reference is removed and timestamps are coarsened to the first day of the month. We use this aggregate to calibrate question difficulty across all learners. We do not attempt re-identification.",
  privacyHowToDelete: "How to delete your account",
  privacyHowToDeleteBody:
    'Sign in, open Settings, then click "Delete account…" in the Danger Zone. The action requires you to type "Delete" to confirm and is irreversible.',
  privacyBackHome: "← Home",
  footerPrivacyLink: "Privacy",
  footerAboutLink: "About",
  settingsTitle: "Settings",
  settingsBackToHome: "← Home",
  settingsProfile: "Profile",
  settingsEmailLabel: "Email",
  settingsLocaleLabel: "Language",
  settingsGameMasterLabel: "Style",
  settingsSubjectLabel: "Subject",
  settingsDangerZone: "Danger Zone",
  settingsDangerZoneBody:
    "Permanently delete your account and personal data. This cannot be undone.",
  settingsDeleteAccountCta: "Delete account\u2026",
  deleteAccountModalTitle: "Delete account",
  deleteAccountModalBodyDeleted:
    "This will permanently delete your account, your email, your profile, and all of your learning progress (rating, mastery). This cannot be undone.",
  deleteAccountModalBodyRetained:
    "Your past responses will be kept in an anonymized form \u2014 without any link to you, with timestamps coarsened to the month \u2014 so we can keep improving question difficulty for everyone. See our Privacy page for details.",
  deleteAccountModalConfirmLabel: "Type \u201cDelete\u201d to confirm",
  deleteAccountModalCancel: "Cancel",
  deleteAccountModalSubmit: "Delete my account",
  deleteAccountModalError: "Couldn\u2019t delete your account. Please try again.",
};

const es: PwaUi = {
  appName: "NotAnyAI",
  language: "Idioma",
  languageAria: "Idioma",
  profile: "Perfil",
  profileAria: "Perfil de aprendiz activo",
  profileOptionGuest: "Invitado",
  subject: "Tema",
  subjectAria: "Curso o tema activo",
  subjectOptionAiLiteracy: "Alfabetización en IA",
  subjectOptionFirstAid: "Primeros auxilios (curso breve)",
  subjectOptionNotany: "¿Qué es notanyai? (demo)",
  subjectOptionEpsoAd8Ai: "EPSO AD8 IA (práctica)",
  subjectFirstAidNote:
    "Consejos prácticos; no sustituyen formación certificada en primeros auxilios ni criterio médico.",
  subjectNotanyNote:
    "Un curso de demostración sobre notanyai: aprendes lo que hace la plataforma usándola.",
  subjectEpsoAd8AiNote: "Solo práctica — no es el banco de preguntas de EPSO ni un curso oficial.",
  questPathLabel: "Ruta",
  questPathAiLiteracy: "Inicio",
  questPathFirstAid: "Hábitos de primera respuesta",
  questPathNotany: "Recorrido del producto",
  questPathEpsoAd8Ai: "Pista del test de campo",
  questStepLine: (step, total, name) => `Paso ${step} de ${total} — ${name}`,
  questTargetHint: (n) => `Apunta a unos ${n}+ en este concepto.`,
  questArcComplete: "Ruta completa. Sigue practicando en el formato que quieras.",
  questArcAria: "Paso actual de la ruta de aprendizaje",
  gameStyle: "Estilo",
  gameStyleAria: "Estilo de dificultad y tutoría",
  gmDefault: "Equilibrado",
  gmPatient: "Paciente",
  gmDrill: "Intenso",
  gmHomeHintPatient:
    "Ritmo suave: ocultamos la racha en esta pantalla. Repaso con respuestas incorrectas más claras.",
  gmHomeHintDrill:
    "Práctica exigente: distractores parecidos y el cuestionario puede repetir preguntas recientes.",
  ratingLabel: "Puntuación",
  streakLabelAria: "Racha diaria",
  streakNone: "sin racha",
  streakDayStreak: (n) => (n === 1 ? "1 día seguido" : `racha de ${n} días`),
  profileStats: "Estadísticas del perfil",
  sessionStats: "Estadísticas de la sesión",
  finalStats: "Estadísticas finales",
  weakestConceptAria: "Concepto más débil",
  focusPrefix: "Sugerencia de enfoque:",
  pickFormatHint: "Elige un formato. Pulsa <kbd>1</kbd>–<kbd>4</kbd> para entrar.",
  resetProgress: "Reiniciar progreso",
  cardMcqTitle: "Quiz rápido",
  cardMcqSub: "Preguntas con 4 opciones adaptadas a tu nivel.",
  cardBackpackTitle: "Llena la mochila",
  cardBackpackSub: "Elige los fragmentos de indicación que encajan.",
  cardSpotlieTitle: "Atrapa la mentira",
  cardSpotlieSub: "Detecta las afirmaciones confiadas pero falsas.",
  cardRedactTitle: "Tacha antes de enviar",
  cardRedactSub: "Oculta lo sensible antes de pegar o enviar.",
  suggestedBadge: "Sugerido",
  sessionCounter: (c, t) => `Sesión ${c}/${t}`,
  miniGameSummaryDetail: (perfect, total, weightedPct) =>
    `${perfect} de ${total} rondas perfectas — ${weightedPct}% de acierto ponderado.`,
  answerOptionsAria: "Opciones de respuesta",
  hintMcq:
    "Toca una opción o pulsa <kbd>1</kbd>–<kbd>4</kbd>. <kbd>N</kbd> siguiente, <kbd>H</kbd> inicio.",
  footer: "Se guarda en este navegador. Añade a la pantalla de inicio para practicar rápido.",
  seeSummary: "Ver resumen",
  nextQuestion: "Siguiente pregunta",
  home: "Inicio",
  backHome: "← Inicio",
  sessionSummary: "Resumen de la sesión",
  newSession: "Nueva sesión",
  summaryScoreLine: (c, t, a) => `${c} / ${t} aciertos (${a} %).`,
  hintSummary: "<kbd>N</kbd> nueva sesión, <kbd>H</kbd> inicio.",
  feedbackCorrect: "Correcto — muy bien.",
  feedbackWrong: "Casi. La respuesta correcta está resaltada arriba.",
  authSignIn: "Iniciar sesión",
  authSignOut: "Cerrar sesión",
  authSignedInAs: "Sesión: {email}",
  authSettings: "Ajustes",
  authModalTitle: "Inicia sesión para guardar tu progreso",
  authModalEmailLabel: "Correo",
  authModalSendButton: "Enviar código",
  authModalSentTitle: "Revisa tu bandeja",
  authModalSentBody: "Enviamos un código de un solo uso a {email}.",
  authModalCodeLabel: "Código de un solo uso",
  authModalVerifyButton: "Verificar código",
  authModalErrorPrefix: "No se pudo iniciar sesión: ",
  authModalCancel: "Cancelar",
  authAuthFailedExpired: "Este enlace venció. Solicita uno nuevo.",
  authMigratedToast: "Bienvenida - tu progreso se guardó en tu cuenta.",
  authSyncFailedToast: "No pudimos sincronizar con la nube. Jugando en modo local por ahora.",
  authDeletedToast: "Tu cuenta ha sido borrada.",
  authSignedOutEmpty: "Inicia sesión para continuar tu progreso.",
  authSignedOutCta: "Iniciar sesión",
  privacyTitle: "Privacidad",
  privacyIntro:
    "NotAnyAI es una herramienta de aprendizaje. Guardamos los datos mínimos necesarios para tu progreso y borramos los datos personales cuando lo pides.",
  privacyWhatWeDelete: "Qué se borra a petición",
  privacyWhatWeDeleteBody:
    "Al borrar tu cuenta eliminamos de forma permanente tu identidad de acceso, tu perfil (nombre, idioma, tema activo) y tu estado del motor (puntuación, racha, dominio por concepto). Esta acción no se puede deshacer.",
  privacyWhatWeKeepAnon: "Qué conservamos de forma anonimizada",
  privacyWhatWeKeepAnonBody:
    "Tus respuestas pasadas se conservan sin ningún vínculo contigo. Se elimina la referencia al usuario y las marcas de tiempo se redondean al primer día del mes. Usamos este agregado para calibrar la dificultad de las preguntas para todos los estudiantes. No intentamos re-identificar a nadie.",
  privacyHowToDelete: "Cómo borrar tu cuenta",
  privacyHowToDeleteBody:
    'Inicia sesión, abre Ajustes y pulsa "Borrar cuenta…" en la Zona de peligro. Tendrás que escribir "Delete" para confirmar; la acción es irreversible.',
  privacyBackHome: "← Inicio",
  footerPrivacyLink: "Privacidad",
  footerAboutLink: "Acerca de",
  settingsTitle: "Ajustes",
  settingsBackToHome: "\u2190 Inicio",
  settingsProfile: "Perfil",
  settingsEmailLabel: "Correo",
  settingsLocaleLabel: "Idioma",
  settingsGameMasterLabel: "Estilo",
  settingsSubjectLabel: "Materia",
  settingsDangerZone: "Zona de peligro",
  settingsDangerZoneBody:
    "Borra permanentemente tu cuenta y tus datos personales. Esta acci\u00f3n no se puede deshacer.",
  settingsDeleteAccountCta: "Borrar cuenta\u2026",
  deleteAccountModalTitle: "Borrar cuenta",
  deleteAccountModalBodyDeleted:
    "Esto borrar\u00e1 permanentemente tu cuenta, tu correo, tu perfil y todo tu progreso de aprendizaje (puntuaci\u00f3n, dominio). Esta acci\u00f3n no se puede deshacer.",
  deleteAccountModalBodyRetained:
    "Tus respuestas pasadas se conservar\u00e1n de forma anonimizada \u2014 sin ning\u00fan v\u00ednculo contigo, con marcas de tiempo redondeadas al mes \u2014 para seguir mejorando la dificultad para todos.",
  deleteAccountModalConfirmLabel: "Escribe \u201cDelete\u201d para confirmar",
  deleteAccountModalCancel: "Cancelar",
  deleteAccountModalSubmit: "Borrar mi cuenta",
  deleteAccountModalError: "No se pudo borrar tu cuenta. Int\u00e9ntalo de nuevo.",
};

const el: PwaUi = {
  ...en,
  language: "Γλώσσα",
  languageAria: "Γλώσσα",
  profile: "Προφίλ",
  profileAria: "Ενεργό προφίλ εκπαιδευόμενου",
  profileOptionGuest: "Επισκέπτης",
  subject: "Θέμα",
  subjectAria: "Ενεργό μάθημα ή θέμα",
  subjectOptionAiLiteracy: "AI literacy",
  subjectOptionFirstAid: "Πρώτες βοήθειες (σύντομο μάθημα)",
  subjectOptionNotany: "Τι είναι το notanyai; (demo)",
  subjectOptionEpsoAd8Ai: "EPSO AD8 AI (εξάσκηση)",
  subjectFirstAidNote:
    "Μόνο πρακτικές συμβουλές — δεν υποκαθιστά πιστοποιημένη εκπαίδευση πρώτων βοηθειών ή ιατρική συμβουλή.",
  subjectNotanyNote:
    "Demo μάθημα για το ίδιο το notanyai: μαθαίνεις τι κάνει η πλατφόρμα χρησιμοποιώντας την.",
  subjectEpsoAd8AiNote: "Μόνο εξάσκηση — δεν είναι η τράπεζα θεμάτων της EPSO ούτε επίσημο μάθημα.",
  questPathLabel: "Διαδρομή μάθησης",
  questPathAiLiteracy: "Εκκίνηση",
  questPathFirstAid: "Συνήθειες πρώτης ανταπόκρισης",
  questPathNotany: "Περιήγηση προϊόντος",
  questPathEpsoAd8Ai: "Διαδρομή του field test",
  gameStyle: "Στυλ",
  gameStyleAria: "Στυλ δυσκολίας και καθοδήγησης",
  gmDefault: "Ισορροπημένο",
  gmPatient: "Ήπιο",
  gmDrill: "Έντονο",
  ratingLabel: "Βαθμολογία",
  streakLabelAria: "Ημερήσιο streak",
  streakNone: "χωρίς streak",
  streakDayStreak: (n) => `${n}-ημερών streak`,
  profileStats: "Στατιστικά προφίλ",
  sessionStats: "Στατιστικά συνεδρίας",
  finalStats: "Τελικά στατιστικά",
  weakestConceptAria: "Πιο αδύναμη έννοια",
  focusPrefix: "Πρόταση εστίασης:",
  pickFormatHint: "Διάλεξε format. Πάτησε <kbd>1</kbd>–<kbd>4</kbd> για άμεση έναρξη.",
  resetProgress: "Επαναφορά προόδου",
  cardMcqTitle: "Γρήγορο κουίζ",
  cardMcqSub: "Ερωτήσεις 4 επιλογών προσαρμοσμένες στο επίπεδό σου.",
  cardBackpackTitle: "Γέμισε το Backpack",
  cardBackpackSub: "Διάλεξε τα σωστά κομμάτια για το prompt.",
  cardSpotlieTitle: "Βρες το ψέμα",
  cardSpotlieSub: "Εντόπισε τις σίγουρες αλλά λανθασμένες δηλώσεις.",
  cardRedactTitle: "Απόκρυψη πριν την αποστολή",
  cardRedactSub: "Κρύψε τα ευαίσθητα πριν κάνεις επικόλληση.",
  suggestedBadge: "Προτεινόμενο",
  sessionSummary: "Περίληψη συνεδρίας",
  newSession: "Νέα συνεδρία",
  feedbackCorrect: "Σωστό — μπράβο.",
  feedbackWrong: "Σχεδόν. Η σωστή απάντηση επισημαίνεται πιο πάνω.",
  authSignIn: "Σύνδεση",
  authSignOut: "Αποσύνδεση",
  authSettings: "Ρυθμίσεις",
  privacyTitle: "Ιδιωτικότητα",
  footerPrivacyLink: "Ιδιωτικότητα",
  footerAboutLink: "Σχετικά",
  settingsTitle: "Ρυθμίσεις",
  settingsLocaleLabel: "Γλώσσα",
  settingsGameMasterLabel: "Στυλ",
  settingsSubjectLabel: "Θέμα",
  settingsDangerZone: "Ζώνη κινδύνου",
  settingsDeleteAccountCta: "Διαγραφή λογαριασμού…",
  deleteAccountModalTitle: "Διαγραφή λογαριασμού",
  deleteAccountModalCancel: "Ακύρωση",
  deleteAccountModalSubmit: "Διαγραφή λογαριασμού",
};

const table: Readonly<Record<Locale, PwaUi>> = { en, es, el };

export function getPwaUi(locale: Locale = DEFAULT_LOCALE): PwaUi {
  return table[locale] ?? en;
}
